import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Car, Plus, X, TrendingUp, MapPin, Calendar, User, DollarSign, Fuel, Gauge } from "lucide-react";
import { api } from "../lib/api";
import RentalDocumentModal from "./RentalDocumentModal";

/**
 * CarRentalTab — dispatcher-side car rental workspace.
 * 3 sub-sections: Dashboard · Fleet · Rentals.
 * Uses the /api/admin/rental/* endpoints (rentals.py router).
 */
export default function CarRentalTab() {
  const [sub, setSub] = useState("dashboard");
  return (
    <section className="pb-20 px-6 md:px-12" data-testid="car-rental-tab">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h2 className="font-serif text-2xl text-[#F5F2EB]">Car Rental</h2>
          <p className="text-xs text-[#71717A] mt-1 uppercase tracking-[0.18em]">
            Fleet inventory · Rental reservations · Checkout &amp; return
          </p>
        </div>
        <div className="flex gap-2 mb-8 border-b border-white/5 overflow-x-auto">
          {[
            ["dashboard", "Dashboard"],
            ["fleet", "Fleet"],
            ["rentals", "Rentals"],
            ["customers", "Customers"],
            ["legal", "Legal"],
            ["settings", "Settings"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`rental-subtab-${k}`}
              onClick={() => setSub(k)}
              className={`px-4 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                sub === k ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#71717A] hover:text-[#F5F2EB]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <TuroConfigWarning onGoTo={setSub}/>
        {sub === "dashboard" && <RentalDashboard />}
        {sub === "fleet" && <RentalFleet />}
        {sub === "rentals" && <RentalReservations />}
        {sub === "customers" && <RentalCustomers />}
        {sub === "legal" && <RentalLegal />}
        {sub === "settings" && <RentalSettings />}
      </div>
    </section>
  );
}

/* ────────── Turo misconfiguration warning ──────────
   Preview and production have separate databases — vehicle iCal URLs and the
   integration toggle must be set in EACH environment. This banner catches the
   two silent-failure states so Turo bookings never quietly stop syncing. */
function TuroConfigWarning({ onGoTo }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get("/admin/rental/turo/status").then(r => setSt(r.data)).catch(() => {});
  }, []);
  if (!st) return null;

  let msg = null, action = null, target = null;
  if (st.turo_enabled && st.vehicles_wired === 0) {
    msg = "Turo sync is ON but no vehicle has a Turo calendar (iCal) URL — nothing will sync.";
    action = "Add the iCal link on a vehicle in Fleet";
    target = "fleet";
  } else if (!st.turo_enabled && st.vehicles_wired > 0) {
    msg = `${st.vehicles_wired} vehicle${st.vehicles_wired === 1 ? " has" : "s have"} a Turo calendar URL but the Turo integration is OFF — nothing will sync.`;
    action = "Enable Turo in Settings";
    target = "settings";
  }
  if (!msg) return null;

  return (
    <div data-testid="turo-config-warning"
      className="flex items-center gap-3 flex-wrap border border-amber-500/50 bg-amber-500/10 px-4 py-3 mb-6">
      <span className="text-amber-300 text-sm">⚠</span>
      <span className="text-xs text-amber-200 flex-1 min-w-[240px]">
        <b>Turo not syncing:</b> {msg}
      </span>
      <button data-testid="turo-config-warning-action" onClick={() => onGoTo(target)}
        className="text-[10px] uppercase tracking-[0.18em] text-amber-300 border border-amber-500/50 px-3 py-1.5 hover:bg-amber-500/20 transition-colors">
        {action} →
      </button>
    </div>
  );
}


/* ────────── Dashboard ────────── */
function RentalDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/rental/dashboard").then(r => setD(r.data)).catch(() => setD(null));
  }, []);
  if (!d) return <div className="text-[#71717A] text-sm">Loading…</div>;
  const kpi = (label, val, testid) => (
    <div className="border border-white/5 bg-[#0A0A0A] p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] mb-2">{label}</div>
      <div className="font-serif text-3xl text-[#F5F2EB]">{val}</div>
    </div>
  );
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpi("Available cars", d.fleet.available, "rental-kpi-available")}
        {kpi("Rented out", d.fleet.rented, "rental-kpi-rented")}
        {kpi("Utilization", `${d.fleet.utilization_pct}%`, "rental-kpi-utilization")}
        {kpi("MTD revenue", `$${d.mtd.revenue.toFixed(0)}`, "rental-kpi-revenue")}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-white/5 bg-[#0A0A0A] p-5" data-testid="upcoming-pickups">
          <div className="overline mb-4 flex items-center gap-2"><MapPin size={12} className="text-[#D4AF37]"/> Upcoming pickups</div>
          {d.upcoming_pickups.length === 0 ? (
            <div className="text-xs text-[#71717A]">None scheduled.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {d.upcoming_pickups.map(r => (
                <li key={r.rental_id} className="flex justify-between border-b border-white/5 pb-2">
                  <div>
                    <div className="text-[#F5F2EB]">{r.renter_name}</div>
                    <div className="text-[10px] tracking-wide text-[#71717A]">{r.vehicle_name} · {r.rental_code}</div>
                  </div>
                  <div className="text-right text-xs text-[#D4AF37]">{formatDt(r.pickup_datetime)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-white/5 bg-[#0A0A0A] p-5" data-testid="upcoming-returns">
          <div className="overline mb-4 flex items-center gap-2"><Calendar size={12} className="text-[#D4AF37]"/> Returns due</div>
          {d.upcoming_returns.length === 0 ? (
            <div className="text-xs text-[#71717A]">No active rentals.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {d.upcoming_returns.map(r => (
                <li key={r.rental_id} className="flex justify-between border-b border-white/5 pb-2">
                  <div>
                    <div className="text-[#F5F2EB]">{r.renter_name}</div>
                    <div className="text-[10px] tracking-wide text-[#71717A]">{r.vehicle_name} · {r.rental_code}</div>
                  </div>
                  <div className="text-right text-xs text-[#D4AF37]">{formatDt(r.return_datetime)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────── Fleet ────────── */
function RentalFleet() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);   // vehicle object (or {} for new)
  const load = () => api.get("/admin/rental/fleet").then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const statusColor = {
    available:    "text-emerald-400 border-emerald-400/40 bg-emerald-400/5",
    rented:       "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/5",
    maintenance:  "text-orange-400 border-orange-400/40 bg-orange-400/5",
    retired:      "text-[#71717A] border-white/10 bg-white/5",
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-xs text-[#71717A] uppercase tracking-[0.18em]">{items.length} vehicle{items.length === 1 ? "" : "s"}</div>
        <button
          data-testid="rental-fleet-new-btn"
          onClick={() => setEditing({})}
          className="btn-gold text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-2"
        ><Plus size={13}/> Add vehicle</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(v => (
          <button
            key={v.rental_vehicle_id}
            onClick={() => setEditing(v)}
            data-testid={`rental-veh-${v.rental_vehicle_id}`}
            className="text-left border border-white/5 bg-[#0A0A0A] p-4 hover:border-[#D4AF37]/40 transition-colors"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="font-serif text-lg text-[#F5F2EB]">{v.year} {v.make} {v.model}</div>
              <div className={`text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border ${statusColor[v.status] || statusColor.available}`}>
                {v.status}
              </div>
            </div>
            <div className="text-xs text-[#71717A] space-y-1">
              <div><Car size={11} className="inline mr-1"/> {v.body_type} · {v.color || "—"} · {v.seats} seat</div>
              <div><Gauge size={11} className="inline mr-1"/> Plate {v.license_plate} · {v.transmission}</div>
              <div><DollarSign size={11} className="inline mr-1"/> ${v.daily_rate}/day · Deposit ${v.security_deposit}</div>
            </div>
          </button>
        ))}
        {items.length === 0 && (
          <div className="col-span-full border border-dashed border-white/10 p-10 text-center text-[#71717A] text-sm">
            No rental vehicles yet. Add your first one to get started.
          </div>
        )}
      </div>
      {editing !== null && (
        <VehicleModal
          vehicle={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// Stable field component — declared OUTSIDE VehicleModal so React keeps the same
// component reference across re-renders. Fixes mobile keyboard collapsing after
// every keystroke because the inner input was being unmounted + remounted.
function VF({ label, k, type = "text", options, f, upd }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {options ? (
        <select className="field-input" data-testid={`veh-${k}`} value={f[k] || ""} onChange={e => upd(k, e.target.value)}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} className="field-input" data-testid={`veh-${k}`} value={f[k] ?? ""} onChange={e => upd(k, e.target.value)}/>
      )}
    </div>
  );
}


function VehicleModal({ vehicle, onClose, onSaved }) {
  const isNew = !vehicle.rental_vehicle_id;
  const [f, setF] = useState({
    make: "", model: "", year: new Date().getFullYear(), color: "", license_plate: "",
    body_type: "sedan", seats: 5, transmission: "automatic", fuel_type: "gasoline",
    daily_rate: 65, weekly_rate: 400, monthly_rate: 1400,
    mileage_included_per_day: 200, extra_mile_rate: 0.35,
    security_deposit: 250, odometer: 0, notes: "",
    ...vehicle,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!f.make || !f.model || !f.license_plate) { toast.error("Make, model & plate are required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/rental/fleet", {
          ...f,
          year: Number(f.year), seats: Number(f.seats), odometer: Number(f.odometer),
          daily_rate: Number(f.daily_rate), weekly_rate: Number(f.weekly_rate), monthly_rate: Number(f.monthly_rate),
          mileage_included_per_day: Number(f.mileage_included_per_day), extra_mile_rate: Number(f.extra_mile_rate),
          security_deposit: Number(f.security_deposit),
        });
        toast.success("Vehicle added");
      } else {
        const patch = {};
        Object.keys(f).forEach(k => {
          if (f[k] !== vehicle[k]) patch[k] = ["year","seats","odometer","daily_rate","weekly_rate","monthly_rate","mileage_included_per_day","extra_mile_rate","security_deposit"].includes(k) ? Number(f[k]) : f[k];
        });
        await api.patch(`/admin/rental/fleet/${vehicle.rental_vehicle_id}`, patch);
        toast.success("Vehicle updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this vehicle from the fleet?")) return;
    try {
      await api.delete(`/admin/rental/fleet/${vehicle.rental_vehicle_id}`);
      toast.success("Vehicle deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="rental-veh-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start px-6 py-5 border-b border-white/5">
          <div>
            <div className="overline">{isNew ? "New vehicle" : "Edit vehicle"}</div>
            <div className="font-serif text-xl text-[#F5F2EB] mt-1">
              {isNew ? "Add to rental fleet" : `${f.year} ${f.make} ${f.model}`}
            </div>
          </div>
          <button data-testid="veh-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <VF label="Make" k="make" f={f} upd={upd}/>
          <VF label="Model" k="model" f={f} upd={upd}/>
          <VF label="Year" k="year" type="number" f={f} upd={upd}/>
          <VF label="Color" k="color" f={f} upd={upd}/>
          <VF label="License plate" k="license_plate" f={f} upd={upd}/>
          <VF label="VIN (optional)" k="vin" f={f} upd={upd}/>
          <VF label="Body type" k="body_type" options={["sedan","suv","van","truck","coupe","hatchback","convertible","other"]} f={f} upd={upd}/>
          <VF label="Seats" k="seats" type="number" f={f} upd={upd}/>
          <VF label="Transmission" k="transmission" options={["automatic","manual"]} f={f} upd={upd}/>
          <VF label="Fuel" k="fuel_type" options={["gasoline","premium","diesel","hybrid","electric"]} f={f} upd={upd}/>
          {!isNew && <VF label="Status" k="status" options={["available","rented","maintenance","retired"]} f={f} upd={upd}/>}
          <div className="sm:col-span-2 border-t border-white/5 my-2"/>
          <VF label="Daily rate ($)" k="daily_rate" type="number" f={f} upd={upd}/>
          <VF label="Weekly rate ($)" k="weekly_rate" type="number" f={f} upd={upd}/>
          <VF label="Monthly rate ($)" k="monthly_rate" type="number" f={f} upd={upd}/>
          <VF label="Security deposit ($)" k="security_deposit" type="number" f={f} upd={upd}/>
          <VF label="Miles included / day" k="mileage_included_per_day" type="number" f={f} upd={upd}/>
          <VF label="Extra mile rate ($)" k="extra_mile_rate" type="number" f={f} upd={upd}/>
          <VF label="Current odometer" k="odometer" type="number" f={f} upd={upd}/>
          <div className="sm:col-span-2">
            <label className="field-label">Notes</label>
            <textarea rows={2} className="field-input" data-testid="veh-notes" value={f.notes || ""} onChange={e => upd("notes", e.target.value)}/>
          </div>
          <div className="sm:col-span-2 border-t border-white/5 my-2"/>
          <div className="sm:col-span-2">
            <label className="field-label">Turo listing iCal URL <span className="text-[#71717A] normal-case tracking-normal">— optional</span></label>
            <input
              className="field-input"
              data-testid="veh-turo-ical"
              placeholder="https://turo.com/api/listings/.../reservations.ics"
              value={f.turo_ical_url || ""}
              onChange={e => upd("turo_ical_url", e.target.value)}
            />
            <div className="text-[10px] text-[#71717A] mt-1 leading-relaxed">
              Copy from your Turo host dashboard → Vehicle → Calendar → Export iCal. Bookings sync every night at 03:00 UTC.
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Turo listing URL <span className="text-[#71717A] normal-case tracking-normal">— optional</span></label>
            <input
              className="field-input"
              data-testid="veh-turo-listing"
              placeholder="https://turo.com/us/en/car-rental/.../{vehicle-id}"
              value={f.turo_listing_url || ""}
              onChange={e => upd("turo_listing_url", e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-between px-6 py-4 border-t border-white/5">
          <div>
            {!isNew && (
              <button data-testid="veh-delete" onClick={remove} className="text-[11px] uppercase tracking-[0.22em] text-red-400 hover:underline">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button data-testid="veh-cancel" onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
            <button data-testid="veh-save" onClick={submit} disabled={saving} className="btn-gold text-[11px]">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────── Rentals ────────── */
function RentalReservations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/admin/rental/rentals", { params: { status: filter === "all" ? undefined : filter } })
    .then(r => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const statusPill = {
    reserved: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30",
    active:   "bg-emerald-400/10 text-emerald-400 border-emerald-400/30",
    returned: "bg-white/5 text-[#71717A] border-white/10",
    cancelled:"bg-red-400/10 text-red-400 border-red-400/30",
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex gap-1">
          {["all","reserved","active","returned","cancelled"].map(k => (
            <button
              key={k}
              data-testid={`rental-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border ${
                filter === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#71717A] hover:text-[#F5F2EB]"
              }`}
            >{k}</button>
          ))}
        </div>
        <button
          data-testid="rental-new-btn"
          onClick={() => setCreating(true)}
          className="btn-gold text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-2"
        ><Plus size={13}/> New rental</button>
      </div>
      <div className="border border-white/5 bg-[#0A0A0A] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-[10px] uppercase tracking-[0.18em] text-[#71717A]">
            <tr>
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Renter</th>
              <th className="text-left px-4 py-3">Vehicle</th>
              <th className="text-left px-4 py-3">Pickup → Return</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr
                key={r.rental_id}
                onClick={() => setSelected(r)}
                data-testid={`rental-row-${r.rental_id}`}
                className="border-t border-white/5 hover:bg-white/5 cursor-pointer"
              >
                <td className="px-4 py-3 font-mono text-[#D4AF37]">
                  {r.rental_code}
                  {r.source === "turo" && (
                    <span data-testid={`turo-badge-${r.rental_id}`}
                      className="ml-2 align-middle text-[8px] font-sans font-bold uppercase tracking-[0.2em] px-1.5 py-0.5 border border-amber-500/50 text-amber-600 bg-amber-400/10">
                      Turo
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[#F5F2EB]">
                  {r.renter_name}
                  <div className="text-[10px] text-[#71717A]">{r.renter_phone}</div>
                </td>
                <td className="px-4 py-3 text-[#F5F2EB]">
                  {r.vehicle_name}
                  <div className="text-[10px] text-[#71717A]">{r.vehicle_plate}</div>
                </td>
                <td className="px-4 py-3 text-xs text-[#F5F2EB]">
                  {formatDt(r.pickup_datetime)} <span className="text-[#71717A]">→</span> {formatDt(r.return_datetime)}
                  <div className="text-[10px] text-[#71717A]">{r.pricing?.days || 1} day{(r.pricing?.days || 1) === 1 ? "" : "s"}</div>
                </td>
                <td className="px-4 py-3 text-right text-[#D4AF37]">${(r.grand_total || r.total || 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[9px] uppercase tracking-[0.22em] px-2 py-1 border ${statusPill[r.status]}`}>{r.status}</span>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717A]">No rentals yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {creating && <NewRentalModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }}/>}
      {selected && <RentalDocumentModal rental={selected} onClose={() => setSelected(null)} onChange={() => { load(); setSelected(null); }}/>}
    </div>
  );
}

function NewRentalModal({ onClose, onCreated }) {
  const [fleet, setFleet] = useState([]);
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dlPhotoData, setDlPhotoData] = useState(null);
  const [f, setF] = useState({
    vehicle_id: "",
    renter_name: "", renter_phone: "", renter_email: "",
    renter_dl_number: "", renter_dl_state: "", renter_dl_expiry: "",
    renter_dob: "", renter_address: "",
    insurance_provider: "", insurance_policy: "",
    pickup_datetime: "", return_datetime: "",
    pickup_location: "Delux Limo HQ · Milwaukee", return_location: "Delux Limo HQ · Milwaukee",
    rate_type: "daily", additional_drivers: 0,
    insurance_option_id: "", insurance_option_label: "", insurance_option_daily_fee: 0,
    discount: 0, notes: "",
    // Recurring series
    is_recurring: false,
    recurrence: "weekly",
    series_end_date: "",
    series_label: "",
    custom_daily_rate: "",
  });
  const upd = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    api.get("/admin/rental/fleet", { params: { status: "available" } })
      .then(r => setFleet(r.data || [])).catch(() => setFleet([]));
    api.get("/admin/rental/settings")
      .then(r => setInsuranceOptions((r.data.insurance_options || []).filter(o => o.enabled)))
      .catch(() => setInsuranceOptions([]));
  }, []);

  // Live pricing preview
  useEffect(() => {
    if (!f.vehicle_id || !f.pickup_datetime || !f.return_datetime) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/admin/rental/pricing-preview", {
          vehicle_id: f.vehicle_id,
          pickup_datetime: f.pickup_datetime,
          return_datetime: f.return_datetime,
          rate_type: f.rate_type,
          additional_drivers: Number(f.additional_drivers || 0),
          insurance_option_daily_fee: Number(f.insurance_option_daily_fee || 0),
          discount: Number(f.discount || 0),
        });
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [f.vehicle_id, f.pickup_datetime, f.return_datetime, f.rate_type, f.additional_drivers, f.insurance_option_daily_fee, f.discount]);

  const pickInsurance = (id) => {
    if (!id) {
      setF(prev => ({ ...prev, insurance_option_id: "", insurance_option_label: "", insurance_option_daily_fee: 0 }));
      return;
    }
    const o = insuranceOptions.find(x => x.id === id);
    if (o) setF(prev => ({ ...prev, insurance_option_id: o.id, insurance_option_label: o.label, insurance_option_daily_fee: o.daily_fee }));
  };

  const handleDlUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Photo too large — please compress to under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setDlPhotoData(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!f.vehicle_id) { toast.error("Pick a vehicle"); return; }
    if (!f.renter_name || !f.renter_phone) { toast.error("Renter name & phone required"); return; }
    if (!f.pickup_datetime || !f.return_datetime) { toast.error("Pickup & return date/time required"); return; }
    if (f.is_recurring && !f.series_end_date) { toast.error("Set the series end date"); return; }
    setSaving(true);
    try {
      const basePayload = {
        ...f,
        additional_drivers: Number(f.additional_drivers || 0),
        discount: Number(f.discount || 0),
        insurance_option_daily_fee: Number(f.insurance_option_daily_fee || 0),
        custom_daily_rate: f.custom_daily_rate === "" ? null : Number(f.custom_daily_rate),
      };
      if (f.is_recurring) {
        const anchor = new Date(f.pickup_datetime);
        const payload = { ...basePayload,
          recurrence: f.recurrence,
          weekday: anchor.getDay() === 0 ? 6 : anchor.getDay() - 1,    // JS Sun=0 → backend Mon=0…Sun=6
          series_end_date: f.series_end_date,
          series_label: f.series_label || "",
          skip_overlap: true,
        };
        const r = await api.post("/admin/rental/rentals/recurring", payload);
        toast.success(`Series created · ${r.data.series_code} · ${r.data.created} rentals (skipped ${r.data.skipped})`);
        onCreated();
        return;
      }
      const r = await api.post("/admin/rental/rentals", basePayload);
      // Attach DL photo (if any)
      if (dlPhotoData) {
        try { await api.post(`/admin/rental/rentals/${r.data.rental_id}/upload-dl`, { photo_base64: dlPhotoData, side: "front" }); }
        catch { toast.error("DL photo upload failed — rental created without it"); }
      }
      toast.success(`Rental created · ${r.data.rental_code}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="rental-new-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start px-6 py-5 border-b border-white/5">
          <div>
            <div className="overline">New rental</div>
            <div className="font-serif text-xl text-[#F5F2EB] mt-1">Book a car rental</div>
          </div>
          <button data-testid="rental-new-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>

        {/* Vehicle */}
        <div className="p-6 border-b border-white/5">
          <div className="overline mb-3">Vehicle</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {fleet.map(v => (
              <button
                key={v.rental_vehicle_id}
                onClick={() => upd("vehicle_id", v.rental_vehicle_id)}
                data-testid={`pick-veh-${v.rental_vehicle_id}`}
                className={`text-left border p-3 transition-colors ${
                  f.vehicle_id === v.rental_vehicle_id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 hover:border-white/30"
                }`}
              >
                <div className="font-serif text-[#F5F2EB]">{v.year} {v.make} {v.model}</div>
                <div className="text-[10px] text-[#71717A] mt-1">{v.body_type} · {v.color} · ${v.daily_rate}/day</div>
              </button>
            ))}
            {fleet.length === 0 && <div className="col-span-full text-xs text-[#71717A]">No vehicles available. Add one in Fleet.</div>}
          </div>
        </div>

        {/* Renter */}
        <div className="p-6 border-b border-white/5">
          <div className="overline mb-3">Renter</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div><label className="field-label">Full name</label><input className="field-input" data-testid="renter-name" value={f.renter_name} onChange={e => upd("renter_name", e.target.value)}/></div>
            <div><label className="field-label">Phone</label><input className="field-input" data-testid="renter-phone" value={f.renter_phone} onChange={e => upd("renter_phone", e.target.value)}/></div>
            <div><label className="field-label">Email</label><input className="field-input" data-testid="renter-email" value={f.renter_email} onChange={e => upd("renter_email", e.target.value)}/></div>
            <div><label className="field-label">Address</label><input className="field-input" data-testid="renter-address" value={f.renter_address} onChange={e => upd("renter_address", e.target.value)}/></div>
            <div><label className="field-label">Driver&apos;s license #</label><input className="field-input" data-testid="renter-dl-number" value={f.renter_dl_number} onChange={e => upd("renter_dl_number", e.target.value)}/></div>
            <div><label className="field-label">DL state</label><input className="field-input" data-testid="renter-dl-state" value={f.renter_dl_state} onChange={e => upd("renter_dl_state", e.target.value)}/></div>
            <div><label className="field-label">DL expiry</label><input type="date" className="field-input" data-testid="renter-dl-expiry" value={f.renter_dl_expiry || ""} onChange={e => upd("renter_dl_expiry", e.target.value)}/></div>
            <div><label className="field-label">Date of birth</label><input type="date" className="field-input" data-testid="renter-dob" value={f.renter_dob || ""} onChange={e => upd("renter_dob", e.target.value)}/></div>
            <div><label className="field-label">Insurance provider</label><input className="field-input" data-testid="renter-ins-provider" value={f.insurance_provider} onChange={e => upd("insurance_provider", e.target.value)}/></div>
            <div><label className="field-label">Insurance policy #</label><input className="field-input" data-testid="renter-ins-policy" value={f.insurance_policy} onChange={e => upd("insurance_policy", e.target.value)}/></div>
          </div>
        </div>

        {/* Dates & pricing */}
        <div className="p-6 border-b border-white/5">
          <div className="overline mb-3">Rental period &amp; options</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div><label className="field-label">Pickup</label><input type="datetime-local" className="field-input" data-testid="rental-pickup-dt" value={f.pickup_datetime} onChange={e => upd("pickup_datetime", e.target.value)}/></div>
            <div><label className="field-label">Return</label><input type="datetime-local" className="field-input" data-testid="rental-return-dt" value={f.return_datetime} onChange={e => upd("return_datetime", e.target.value)}/></div>
            <div><label className="field-label">Rate type</label>
              <select className="field-input" data-testid="rental-rate-type" value={f.rate_type} onChange={e => upd("rate_type", e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div><label className="field-label">Additional drivers</label><input type="number" min="0" className="field-input" data-testid="rental-add-drivers" value={f.additional_drivers} onChange={e => upd("additional_drivers", e.target.value)}/></div>
            <div className="md:col-span-2">
              <label className="field-label">Damage &amp; liability coverage</label>
              <select
                className="field-input"
                data-testid="rental-insurance-option"
                value={f.insurance_option_id}
                onChange={e => pickInsurance(e.target.value)}
              >
                <option value="">No coverage</option>
                {insuranceOptions.map(o => (
                  <option key={o.id} value={o.id}>{o.label} — ${o.daily_fee}/day</option>
                ))}
              </select>
              {insuranceOptions.length === 0 && (
                <div className="text-[10px] text-[#71717A] mt-1">No coverage options configured. Add them in <span className="text-[#D4AF37]">Settings → Damage &amp; liability</span>.</div>
              )}
            </div>
            <div><label className="field-label">Discount ($)</label><input type="number" min="0" step="0.01" className="field-input" data-testid="rental-discount" value={f.discount} onChange={e => upd("discount", e.target.value)}/></div>
            <div><label className="field-label">Pickup location</label><input className="field-input" data-testid="rental-pickup-loc" value={f.pickup_location} onChange={e => upd("pickup_location", e.target.value)}/></div>
            <div className="md:col-span-2"><label className="field-label">Custom daily rate override ($) <span className="text-[#71717A] normal-case tracking-normal">— optional</span></label><input type="number" min="0" step="0.01" className="field-input" data-testid="rental-custom-rate" placeholder={`Vehicle default applies if blank`} value={f.custom_daily_rate} onChange={e => upd("custom_daily_rate", e.target.value)}/></div>
            <div className="md:col-span-2"><label className="field-label">Notes</label><textarea rows={2} className="field-input" data-testid="rental-notes" value={f.notes} onChange={e => upd("notes", e.target.value)}/></div>

            {/* Recurring reservation */}
            <div className="md:col-span-2 border-t border-white/5 pt-4 mt-2">
              <label className="flex items-center gap-2 text-[#F5F2EB] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="rental-recurring-toggle"
                  checked={f.is_recurring}
                  onChange={e => upd("is_recurring", e.target.checked)}
                  className="w-4 h-4 accent-[#D4AF37]"
                />
                <span className="text-sm">Make this a recurring reservation</span>
              </label>
              {f.is_recurring && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 pl-6 border-l-2 border-[#D4AF37]/30">
                  <div>
                    <label className="field-label">Frequency</label>
                    <select className="field-input" data-testid="rental-recurrence" value={f.recurrence} onChange={e => upd("recurrence", e.target.value)}>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 weeks</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">End date</label>
                    <input type="date" className="field-input" data-testid="rental-series-end" value={f.series_end_date} onChange={e => upd("series_end_date", e.target.value)}/>
                  </div>
                  <div>
                    <label className="field-label">Label <span className="text-[#71717A] normal-case tracking-normal">— optional</span></label>
                    <input className="field-input" data-testid="rental-series-label" placeholder="e.g. Sprinter · Sunday · $650" value={f.series_label} onChange={e => upd("series_label", e.target.value)}/>
                  </div>
                  <div className="md:col-span-3 text-[10px] text-[#71717A]">
                    A rental will be created for every {f.recurrence === "weekly" ? "week" : f.recurrence === "biweekly" ? "2 weeks" : "month"} between the pickup date above and the end date. Overlapping days are skipped automatically.
                  </div>
                </div>
              )}
            </div>

            {/* DL photo upload */}
            <div className="md:col-span-2 border-t border-white/5 pt-4 mt-2">
              <label className="field-label">Driver&apos;s license photo (front)</label>
              <input
                type="file"
                accept="image/*"
                data-testid="rental-dl-photo"
                onChange={handleDlUpload}
                className="text-xs text-[#F5F2EB] file:mr-3 file:py-1.5 file:px-3 file:border file:border-[#D4AF37]/40 file:bg-transparent file:text-[#D4AF37] file:cursor-pointer file:text-[10px] file:uppercase file:tracking-wider"
              />
              {dlPhotoData && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={dlPhotoData} alt="DL preview" className="h-16 border border-white/10"/>
                  <span className="text-[10px] text-emerald-400 uppercase tracking-wider">Ready to upload</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Live pricing */}
        {preview && (
          <div className="p-6 border-b border-white/5 bg-[#D4AF37]/5" data-testid="rental-pricing-preview">
            <div className="overline mb-3">Pricing preview</div>
            <div className="grid grid-cols-2 gap-y-1 text-sm">
              <div className="text-[#71717A]">{preview.days} day(s) × ${preview.rate}</div>
              <div className="text-right text-[#F5F2EB]">${preview.base.toFixed(2)}</div>
              {preview.additional_driver_fee_total > 0 && <><div className="text-[#71717A]">Additional drivers</div><div className="text-right text-[#F5F2EB]">${preview.additional_driver_fee_total.toFixed(2)}</div></>}
              {preview.additional_insurance_fee_total > 0 && <><div className="text-[#71717A]">{f.insurance_option_label || "Damage & liability coverage"}</div><div className="text-right text-[#F5F2EB]">${preview.additional_insurance_fee_total.toFixed(2)}</div></>}
              {preview.discount > 0 && <><div className="text-[#71717A]">Discount</div><div className="text-right text-red-400">-${preview.discount.toFixed(2)}</div></>}
              <div className="text-[#71717A]">Tax (5.5%)</div><div className="text-right text-[#F5F2EB]">${preview.tax.toFixed(2)}</div>
              <div className="text-[#D4AF37] font-bold text-base mt-2">TOTAL</div>
              <div className="text-right text-[#D4AF37] font-bold text-base mt-2">${preview.total.toFixed(2)}</div>
              <div className="text-[10px] text-[#71717A] mt-2">+ ${preview.security_deposit.toFixed(2)} refundable deposit · {preview.mileage_included_total} mi included</div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 px-6 py-4">
          <button data-testid="rental-new-cancel" onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
          <button data-testid="rental-new-submit" onClick={submit} disabled={saving} className="btn-gold text-[11px]">
            {saving ? "Creating…" : "Create rental"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────── Settings ────────── */

const PROVIDER_LABELS = {
  square:       { name: "Square",      blurb: "Charge card, capture deposit, refund — reuses your chauffeur Square account." },
  rental_cover: { name: "Rental Cover",blurb: "Add rentalcover.com as a damage & liability insurance option for your renters." },
  bonzah:       { name: "Bonzah",      blurb: "Connect with Bonzah to provide damage and liability insurance to your customers." },
  allianz:      { name: "Allianz",     blurb: "Connect with Allianz Rental Car Damage Protector as an insurance option." },
  turo:         { name: "Turo",        blurb: "Sync your Turo host reservations into the rental calendar — bookings pull nightly." },
};

function RentalSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/rental/settings").then(r => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  if (!s) return <div className="text-[#71717A] text-sm">Loading…</div>;

  const patch = async (upd) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/rental/settings", upd);
      setS(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const addOption = () => {
    const label = window.prompt("Coverage name (e.g. 'Full Coverage'):");
    if (!label) return;
    const fee = parseFloat(window.prompt("Daily fee ($)? e.g. 25", "20"));
    if (!fee && fee !== 0) return;
    patch({ insurance_options: [...(s.insurance_options || []), { label, daily_fee: fee, description: "", enabled: true }] });
  };
  const removeOption = (id) => {
    if (!window.confirm("Remove this coverage option?")) return;
    patch({ insurance_options: (s.insurance_options || []).filter(o => o.id !== id) });
  };
  const toggleOption = (id, enabled) => {
    patch({ insurance_options: (s.insurance_options || []).map(o => o.id === id ? { ...o, enabled } : o) });
  };
  const toggleProvider = (key, enabled) => {
    patch({ integrations: { [key]: { ...s.integrations[key], enabled } } });
  };

  return (
    <div className="space-y-6" data-testid="rental-settings">
      {/* Payment (Square) */}
      <div className="border border-white/5 bg-[#0A0A0A] p-5">
        <div className="overline mb-3">Payment</div>
        <ProviderRow
          k="square"
          enabled={s.integrations?.square?.enabled}
          onToggle={(v) => toggleProvider("square", v)}
          extra={<span className="text-[10px] uppercase tracking-[0.22em] text-emerald-400">Connected ✓</span>}
          saving={saving}
        />
      </div>

      {/* ID Verification (built-in DL photo capture) */}
      <div className="border border-white/5 bg-[#0A0A0A] p-5">
        <div className="overline mb-3">ID Verification</div>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 border border-white/10 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
            <User size={20}/>
          </div>
          <div className="flex-1">
            <div className="text-[#F5F2EB] font-semibold">Driver&apos;s License photo capture</div>
            <div className="text-xs text-[#71717A] mt-1">
              Front-of-license photo is captured at rental creation and attached to the rental record.
              Third-party ID services (Stripe Identity, Vouched) can be added later if needed.
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-400 flex-shrink-0">Built-in ✓</span>
        </div>
      </div>

      {/* Company signature — stamped on rental agreement PDFs */}
      <div className="border border-white/5 bg-[#0A0A0A] p-5" data-testid="company-signature-settings">
        <div className="overline mb-3">Dispatcher signature</div>
        <div className="text-xs text-[#71717A] mb-4">
          This signature is embedded as the &ldquo;Authorized Representative&rdquo; signature on every rental
          agreement PDF and in the rental document view.
        </div>
        <div className="flex flex-wrap items-center gap-5">
          {s.company_signature ? (
            <img src={`data:image/png;base64,${s.company_signature}`} alt="Company signature"
                 className="h-16 bg-white px-3 py-1 border border-white/10" data-testid="company-signature-preview"/>
          ) : (
            <div className="text-xs text-[#71717A] italic">No signature on file yet.</div>
          )}
          <label className="text-[10px] uppercase tracking-[0.22em] px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/5 cursor-pointer">
            {s.company_signature ? "Replace signature" : "Upload signature"}
            <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="company-signature-upload"
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 500_000) { toast.error("Signature image too large — keep under 500KB"); return; }
                const reader = new FileReader();
                reader.onload = (ev) => patch({ company_signature: String(ev.target.result).split(",", 2)[1] });
                reader.readAsDataURL(file);
              }}/>
          </label>
        </div>
      </div>

      {/* Damage & Liability Insurance — custom options */}
      <div className="border border-white/5 bg-[#0A0A0A] p-5">
        <div className="flex justify-between items-center mb-4">
          <div className="overline">Damage &amp; liability — custom coverage options</div>
          <button
            data-testid="rental-settings-add-option"
            onClick={addOption}
            disabled={saving}
            className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:underline inline-flex items-center gap-1"
          ><Plus size={12}/> Add option</button>
        </div>
        {(s.insurance_options || []).length === 0 ? (
          <div className="text-xs text-[#71717A] italic">No coverage tiers yet — add e.g. &ldquo;Basic CDW · $15/day&rdquo;, &ldquo;Full Coverage · $25/day&rdquo;</div>
        ) : (
          <div className="space-y-2">
            {s.insurance_options.map(o => (
              <div key={o.id} className="flex items-center justify-between border border-white/5 px-3 py-2 text-sm" data-testid={`rental-ins-opt-${o.id}`}>
                <div className="flex-1">
                  <div className="text-[#F5F2EB]">{o.label}</div>
                  <div className="text-[10px] text-[#71717A]">${o.daily_fee}/day</div>
                </div>
                <button
                  onClick={() => toggleOption(o.id, !o.enabled)}
                  disabled={saving}
                  className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border mr-2 ${
                    o.enabled ? "border-emerald-400/40 text-emerald-400" : "border-white/10 text-[#71717A]"
                  }`}
                >{o.enabled ? "On" : "Off"}</button>
                <button
                  onClick={() => removeOption(o.id)}
                  disabled={saving}
                  className="text-[10px] uppercase tracking-[0.22em] text-red-400 hover:underline"
                >Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Third-party insurance framework */}
      <div className="border border-white/5 bg-[#0A0A0A] p-5">
        <div className="overline mb-4">Damage &amp; liability — third-party providers <span className="normal-case tracking-normal text-[#71717A] text-[10px]">(optional referral partners)</span></div>
        <div className="text-[10px] text-[#71717A] uppercase tracking-[0.18em] mb-4">
          Enabled providers appear on the renter's e-sign page as "Protect your rental" buy-links (they purchase directly with the partner). Your <span className="text-[#D4AF37]">Custom Coverage Options above</span> are what actually get charged during rental checkout.
        </div>
        <div className="space-y-2">
          {["rental_cover", "bonzah", "allianz"].map(key => (
            <ProviderRow
              key={key}
              k={key}
              enabled={s.integrations?.[key]?.enabled}
              referralUrl={s.integrations?.[key]?.referral_url}
              onToggle={(v) => toggleProvider(key, v)}
              saving={saving}
            />
          ))}
        </div>
      </div>

      {/* Turo host sync */}
      <div className="border border-white/5 bg-[#0A0A0A] p-5" data-testid="turo-settings">
        <div className="overline mb-4">Marketplace listing — Turo</div>
        <ProviderRow
          k="turo"
          enabled={s.integrations?.turo?.enabled}
          onToggle={(v) => toggleProvider("turo", v)}
          saving={saving}
        />
        {s.integrations?.turo?.enabled && (
          <div className="mt-4 grid grid-cols-1 gap-3 text-xs">
            <div>
              <label className="field-label">Public Turo profile / listing URL</label>
              <input
                className="field-input"
                data-testid="turo-profile-url"
                placeholder="https://turo.com/us/en/host/33693391"
                value={s.integrations.turo.host_profile_url || ""}
                onChange={e => patch({ integrations: { turo: { ...s.integrations.turo, host_profile_url: e.target.value } } })}
              />
            </div>
            {s.integrations.turo.host_profile_url && (
              <a href={s.integrations.turo.host_profile_url} target="_blank" rel="noopener noreferrer"
                 className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline">
                Open your Turo profile →
              </a>
            )}
            <div className="border-t border-white/5 pt-3 mt-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] mb-2">Calendar sync</div>
              <div className="text-[11px] text-[#71717A] mb-2 leading-relaxed">
                Paste each listing&apos;s iCal export URL on the individual vehicle inside <span className="text-[#F5F2EB]">Fleet</span>. Turo bookings will appear on your rental calendar automatically every night at 03:00 UTC.
              </div>
              <TuroSyncControls />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function TuroSyncControls() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => api.get("/admin/rental/turo/status").then(r => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { refresh(); }, []);
  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/rental/turo/sync");
      const s = r.data || {};
      toast.success(`Turo sync · ${s.created || 0} created · ${s.updated || 0} updated · ${s.vehicles_scanned || 0} vehicle(s)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };
  const last = status?.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : "Never";
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="turo-sync-controls">
      <button
        onClick={syncNow}
        disabled={busy}
        data-testid="turo-sync-now"
        className="text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/5 disabled:opacity-50"
      >{busy ? "Syncing…" : "Sync now"}</button>
      <div className="text-[10px] text-[#71717A]">
        <span data-testid="turo-vehicles-wired">{status?.vehicles_wired ?? 0}</span> vehicle(s) wired ·{" "}
        <span data-testid="turo-rentals-total">{status?.turo_rentals_total ?? 0}</span> Turo rental(s) ·{" "}
        Last sync: <span data-testid="turo-last-synced">{last}</span>
      </div>
    </div>
  );
}

function ProviderRow({ k, enabled, onToggle, referralUrl, extra, saving }) {
  const p = PROVIDER_LABELS[k] || { name: k, blurb: "" };
  return (
    <div className="flex items-start gap-3 py-2" data-testid={`provider-row-${k}`}>
      <div className="w-12 h-12 border border-white/10 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
        <ShieldIcon k={k}/>
      </div>
      <div className="flex-1">
        <div className="text-[#F5F2EB] font-semibold">{p.name}</div>
        <div className="text-xs text-[#71717A] mt-1">{p.blurb}</div>
        {referralUrl && !enabled && (
          <a href={referralUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline mt-2 inline-block">
            Request access →
          </a>
        )}
      </div>
      {extra ? extra : (
        <button
          onClick={() => onToggle(!enabled)}
          disabled={saving}
          data-testid={`provider-toggle-${k}`}
          className={`text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border ${
            enabled ? "border-emerald-400/40 text-emerald-400" : "border-[#D4AF37]/40 text-[#D4AF37]"
          }`}
        >{enabled ? "Connected" : "Connect"}</button>
      )}
    </div>
  );
}

function ShieldIcon({ k }) {
  // Simple monogram badges — kept generic (no logo assets required).
  const letters = { rental_cover: "RC", bonzah: "BZ", allianz: "AL", square: "SQ", turo: "T" }[k] || "??";
  return <span className="font-serif text-sm">{letters}</span>;
}



/* ────────── Customers ────────── */
function RentalCustomers() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, verified_id_pct: 0, verified_ins_pct: 0 });
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/rental/customers", { params: { q: q || undefined, archived: tab === "archived" } });
    let list = r.data || [];
    if (tab === "verified") list = list.filter(c => c.id_verified && c.insurance_verified);
    setItems(list);
    const s = await api.get("/admin/rental/customers/summary").then(x => x.data).catch(() => null);
    if (s) setSummary(s);
  };
  useEffect(() => { load(); }, [tab, q]);

  return (
    <div className="space-y-6" data-testid="rental-customers">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Total customers", summary.total], ["Active customers", summary.active], ["Verified insurance", `${summary.verified_ins_pct}%`], ["Verified IDs", `${summary.verified_id_pct}%`]].map(([l, v]) => (
          <div key={l} className="border border-white/5 bg-[#0A0A0A] p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] mb-2">{l}</div>
            <div className="font-serif text-3xl text-[#F5F2EB]">{v}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex gap-1 items-center">
          {[["all","All"],["verified","Verified"],["archived","Archived"]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cust-filter-${k}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border ${tab===k?"border-[#D4AF37] text-[#D4AF37]":"border-white/10 text-[#71717A] hover:text-[#F5F2EB]"}`}>{l}</button>
          ))}
          <input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)}
            className="field-input ml-2 text-[11px] py-1.5" data-testid="cust-search"/>
        </div>
        <button data-testid="cust-add" onClick={() => setEditing({})} className="btn-gold text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-2">
          <Plus size={13}/> Add customer
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map(c => (
          <button key={c.customer_id} onClick={() => setEditing(c)} data-testid={`cust-card-${c.customer_id}`}
            className="text-left border border-white/5 bg-[#0A0A0A] p-4 hover:border-[#D4AF37]/40 transition-colors">
            <div className="flex justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">{c.public_code || "—"}</span>
              <User size={12} className="text-[#71717A]"/>
            </div>
            <div className="font-serif text-[#F5F2EB] mb-2">{c.name}</div>
            <div className="flex flex-wrap gap-1 mb-3">
              <span className={`text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 border ${c.id_verified?"border-emerald-400/40 text-emerald-400 bg-emerald-400/5":"border-red-400/40 text-red-400 bg-red-400/5"}`}>{c.id_verified?"ID verified":"ID not verified"}</span>
              <span className={`text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 border ${c.insurance_verified?"border-emerald-400/40 text-emerald-400 bg-emerald-400/5":"border-red-400/40 text-red-400 bg-red-400/5"}`}>{c.insurance_verified?"Ins verified":"Ins not verified"}</span>
            </div>
            <div className="text-[10px] text-[#71717A]">{c.email || "—"}</div>
            <div className="text-[10px] text-[#71717A]">{c.phone || "—"}</div>
            <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-white/5">
              <div><div className="text-[9px] text-[#71717A] uppercase">Bookings</div><div className="text-[#F5F2EB]">{c.bookings}</div></div>
              <div className="text-right"><div className="text-[9px] text-[#71717A] uppercase">Revenue</div><div className="text-[#D4AF37]">${(c.total_revenue||0).toFixed(2)}</div></div>
            </div>
          </button>
        ))}
        {items.length===0 && <div className="col-span-full text-center text-[#71717A] p-10 border border-dashed border-white/10">No customers yet.</div>}
      </div>
      {editing && <CustomerModal customer={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function CustomerModal({ customer, onClose, onSaved }) {
  const isNew = !customer.customer_id;
  const [f, setF] = useState({
    name: "", phone: "", email: "", dl_number: "", dl_state: "", dl_expiry: "",
    dob: "", address: "", insurance_provider: "", insurance_policy: "", notes: "",
    id_verified: false, insurance_verified: false, archived: false,
    ...customer,
  });
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.name || !f.phone) { toast.error("Name & phone required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/rental/customers", f);
      else {
        const patch = {};
        Object.keys(f).forEach(k => { if (f[k] !== customer[k]) patch[k] = f[k]; });
        await api.patch(`/admin/rental/customers/${customer.customer_id}`, patch);
      }
      toast.success(isNew ? "Customer added" : "Customer updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="cust-modal">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start px-6 py-5 border-b border-white/5">
          <div>
            <div className="overline">{isNew ? "New customer" : "Edit customer"}</div>
            <div className="font-serif text-xl text-[#F5F2EB] mt-1">{isNew ? "Add renter to directory" : f.name}</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div className="sm:col-span-2"><label className="field-label">Full name</label><input className="field-input" data-testid="cust-name" value={f.name} onChange={e=>upd("name",e.target.value)}/></div>
          <div><label className="field-label">Phone</label><input className="field-input" data-testid="cust-phone" value={f.phone} onChange={e=>upd("phone",e.target.value)}/></div>
          <div><label className="field-label">Email</label><input className="field-input" data-testid="cust-email" value={f.email||""} onChange={e=>upd("email",e.target.value)}/></div>
          <div><label className="field-label">Address</label><input className="field-input" value={f.address||""} onChange={e=>upd("address",e.target.value)}/></div>
          <div><label className="field-label">Date of birth</label><input type="date" className="field-input" value={f.dob||""} onChange={e=>upd("dob",e.target.value)}/></div>
          <div><label className="field-label">DL number</label><input className="field-input" value={f.dl_number||""} onChange={e=>upd("dl_number",e.target.value)}/></div>
          <div><label className="field-label">DL state</label><input className="field-input" value={f.dl_state||""} onChange={e=>upd("dl_state",e.target.value)}/></div>
          <div><label className="field-label">DL expiry</label><input type="date" className="field-input" value={f.dl_expiry||""} onChange={e=>upd("dl_expiry",e.target.value)}/></div>
          <div><label className="field-label">Insurance provider</label><input className="field-input" value={f.insurance_provider||""} onChange={e=>upd("insurance_provider",e.target.value)}/></div>
          <div><label className="field-label">Insurance policy</label><input className="field-input" value={f.insurance_policy||""} onChange={e=>upd("insurance_policy",e.target.value)}/></div>
          <div className="sm:col-span-2 flex flex-wrap gap-4 items-center border-t border-white/5 pt-3">
            <label className="flex items-center gap-2 text-[#F5F2EB]"><input type="checkbox" data-testid="cust-id-verified" checked={!!f.id_verified} onChange={e=>upd("id_verified",e.target.checked)} className="w-4 h-4 accent-emerald-400"/> ID verified</label>
            <label className="flex items-center gap-2 text-[#F5F2EB]"><input type="checkbox" data-testid="cust-ins-verified" checked={!!f.insurance_verified} onChange={e=>upd("insurance_verified",e.target.checked)} className="w-4 h-4 accent-emerald-400"/> Insurance verified</label>
            {!isNew && <label className="flex items-center gap-2 text-red-400"><input type="checkbox" checked={!!f.archived} onChange={e=>upd("archived",e.target.checked)} className="w-4 h-4 accent-red-400"/> Archived</label>}
          </div>
          <div className="sm:col-span-2"><label className="field-label">Notes</label><textarea rows={2} className="field-input" value={f.notes||""} onChange={e=>upd("notes",e.target.value)}/></div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/5">
          <button onClick={onClose} className="btn-ghost text-[11px]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="cust-save" className="btn-gold text-[11px]">{saving?"Saving…":"Save"}</button>
        </div>
      </div>
    </div>
  );
}

/* ────────── Legal — Rental Agreement · Cancellation · Policy ────────── */
const DEFAULT_LEGAL = {
  rental_agreement: "RENTAL AGREEMENT\n\nThis Rental Agreement is entered into between Delux Limo Trans Group (Company) and the individual named as Renter on the reservation.\n\n1. VEHICLE & TERM — Company rents the vehicle described in the reservation for the period stated.\n2. AUTHORIZED DRIVERS — Only the Renter and any additional drivers listed may operate the vehicle.\n3. INSURANCE & LIABILITY — Renter must maintain a valid license and proof of insurance.\n4. FUEL, MILEAGE & CONDITION — Vehicle is delivered full and clean; return same or pay fees. Mileage overage per rate sheet.\n5. PROHIBITED USES — No off-road, no smoking, no illegal materials, no ride-share.\n6. SECURITY DEPOSIT — Refundable hold for damages, cleaning, late fees, violations.\n7. LATE RETURN — Fees apply after 30 minutes past scheduled return.\n8. GOVERNING LAW — Wisconsin.",
  cancellation_policy: "CANCELLATION POLICY\n\n• 48+ hours before pickup: 100% refund.\n• 24-48 hours before pickup: 50% refund.\n• Less than 24 hours: no refund.\n• No-shows are billed full reservation.\n• Weather / medical emergencies may be waived at management's discretion.",
  rental_policy: "RENTAL POLICY\n\n• Minimum age: 21 (25 for luxury/premium).\n• Valid driver's license required at pickup.\n• Additional drivers must be listed with license.\n• Non-smoking. Cleaning fee: $250.\n• Damage or accident must be reported immediately.\n• Late returns: hourly overage + $75 late fee after 30 minutes.",
};

function RentalLegal() {
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/rental/settings").then(r => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  if (!s) return <div className="text-[#71717A] text-sm">Loading…</div>;

  const docs = [
    { key: "rental_agreement",     label: "Rental Agreement",     blurb: "Terms & conditions rendered with each rental. Renter signs at pickup." },
    { key: "cancellation_policy",  label: "Cancellation Policy",  blurb: "Refund rules by cancellation window — surfaced at booking and on confirmations." },
    { key: "rental_policy",        label: "Rental Policy",        blurb: "House rules — age, fuel, cleaning, additional drivers, damage reporting." },
  ];

  const save = async (key, text) => {
    try {
      await api.patch("/admin/rental/settings", { [key]: text });
      toast.success(`${docs.find(d=>d.key===key).label} saved`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-4" data-testid="rental-legal">
      {docs.map(d => (
        <div key={d.key} className="border border-white/5 bg-[#0A0A0A] p-5">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0 pr-4">
              <div className="text-[#F5F2EB] font-semibold text-base">{d.label}</div>
              <div className="text-xs text-[#71717A] mt-1">{d.blurb}</div>
              <div className="mt-3 text-[11px] text-[#A1A1AA] max-h-24 overflow-hidden whitespace-pre-wrap">
                {(s[d.key] || DEFAULT_LEGAL[d.key]).slice(0, 220)}…
              </div>
            </div>
            <button
              data-testid={`legal-edit-${d.key}`}
              onClick={() => setEditing({ key: d.key, label: d.label, text: s[d.key] || DEFAULT_LEGAL[d.key] })}
              className="btn-gold text-[11px] uppercase tracking-[0.22em] whitespace-nowrap"
            >Add/edit</button>
          </div>
        </div>
      ))}
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="legal-modal">
          <div className="bg-[#0A0A0A] border border-white/10 max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-start px-6 py-5 border-b border-white/5">
              <div>
                <div className="overline">Editing</div>
                <div className="font-serif text-xl text-[#F5F2EB] mt-1">{editing.label}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
            </div>
            <textarea
              data-testid="legal-textarea"
              value={editing.text}
              onChange={e => setEditing({ ...editing, text: e.target.value })}
              className="flex-1 bg-[#141414] text-[#F5F2EB] text-xs font-mono p-4 border-0 focus:outline-none min-h-[420px] resize-none"
            />
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-white/5">
              <button onClick={() => setEditing(null)} className="btn-ghost text-[11px]">Cancel</button>
              <button data-testid="legal-save" onClick={() => save(editing.key, editing.text)} className="btn-gold text-[11px]">Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function formatDt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
